import { useMemo } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { generateBreadcrumbs, getPageTitle } from '@/utils/routeMapper';
import { useUserByUUID } from '@/hooks/useUserQuery';
import useFetchTeams from '@/hooks/useFetchTeams';
import { useUserLogged } from '@/hooks/useUserLogged';
import { fetchDocumentsSidebar } from '@/apis/fetchDocumentsSidebar';
import { useOrganizationByUUID } from '@/hooks/useOrganizationQuery';
import { useFetchReview } from '@/hooks/usePerformanceReview';
import type { BreadcrumbItem } from '@/types/navigation';
import type headerObject from '@/types/headerObj';
import type { GetConversationsResponse } from '@/types/assistantChat';
import { useQuery } from '@tanstack/react-query';
import { getConversations } from '@/apis/assistantChat';
import { useBoardsStore } from '@/app/(dashboard)/boards/_lib/boardsStore';

interface UseDynamicBreadcrumbsOptions {
  dynamicValues?: Record<string, string>;
  customLabels?: Record<string, string>;
}

interface UseDynamicBreadcrumbsReturn {
  breadcrumbs: BreadcrumbItem[];
  pageTitle: string;
  currentPath: string;
  isLoading: boolean;
}

/**
 * Enhanced hook to generate breadcrumbs with dynamic UUID resolution
 * @param options - Configuration options for breadcrumb generation
 * @returns Object containing breadcrumbs, page title, current path, and loading state
 */
export function useDynamicBreadcrumbs(
  options: UseDynamicBreadcrumbsOptions = {}
): UseDynamicBreadcrumbsReturn {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { dynamicValues = {}, customLabels = {} } = options;
  const { organization, user } = useUserLogged();
  const ellieConversationId = pathname?.startsWith('/ask-ellie')
    ? searchParams?.get('conversation')
    : null;
  const { data: ellieConversations } = useQuery<GetConversationsResponse>({
    queryKey: ['ellie-conversations', user?.UserUUID, organization?.OrgUUID],
    queryFn: () =>
      getConversations({
        userUUID: user!.UserUUID!,
        orgUUID: organization!.OrgUUID!,
        limit: 50,
        offset: 0,
      }),
    enabled: Boolean(
      ellieConversationId && user?.UserUUID && organization?.OrgUUID
    ),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
  const ellieChatTitle = useMemo(() => {
    if (!ellieConversationId || !ellieConversations) return null;
    return (
      ellieConversations.conversations.find(
        (c) => c.uuid === ellieConversationId
      )?.title || null
    );
  }, [ellieConversationId, ellieConversations]);

  // Extract UUIDs from the pathname
  const pathSegments = pathname.split('/').filter(Boolean);
  const userId = pathSegments.find(
    (segment) =>
      (pathSegments[pathSegments.indexOf(segment) - 1] === 'u' ||
        pathSegments[pathSegments.indexOf(segment) - 1] === 'team-insight' ||
        pathSegments[pathSegments.indexOf(segment) - 1] === 'members') &&
      segment.match(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      )
  );
  const teamUUID = pathSegments.find(
    (segment) =>
      pathSegments[pathSegments.indexOf(segment) - 1] === 'team-overview' &&
      segment.match(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      )
  );

  const orgUUID = pathSegments.find(
    (segment) =>
      pathSegments[pathSegments.indexOf(segment) - 1] === 'leaderboard' &&
      segment.match(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      )
  );

  const reportUUID = pathSegments.find(
    (segment) =>
      pathSegments[pathSegments.indexOf(segment) - 1] === 'report' &&
      pathSegments[pathSegments.indexOf(segment) - 2] ===
        'individual-insights' &&
      segment.match(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      )
  );
  const docUUID = pathSegments.find(
    (segment) =>
      pathSegments[pathSegments.indexOf(segment) - 2] === 'documentation' &&
      segment.match(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      )
  );

  const boardId = pathSegments.find(
    (segment) =>
      pathSegments[pathSegments.indexOf(segment) - 1] === 'boards' &&
      segment.match(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      )
  );

  // Resolve the board title from the client store (boards are created/loaded
  // there); falls back to the static "Board" label until the store hydrates.
  const boardTitle = useBoardsStore((s) =>
    boardId ? s.boards.find((b) => b.id === boardId)?.title : undefined
  );

  // Get the IntroArtifactUUID (repository identifier) for fetching document structure
  const introArtifactUUID = pathSegments.find(
    (segment) =>
      pathSegments[pathSegments.indexOf(segment) - 1] === 'documentation' &&
      segment !== 'documentation'
  );

  // Fetch user data if userId is present
  const { data: userData, isLoading: userLoading } = useUserByUUID(
    userId || '',
    {} as headerObject
  );

  // Fetch teams data if teamUUID is present
  const { data: teamsData, isLoading: teamsLoading } = useFetchTeams(
    organization?.OrgUUID
  );

  // Fetch organization data if orgUUID is present
  const { data: orgData, isLoading: orgLoading } = useOrganizationByUUID(
    orgUUID || ''
  );

  // Fetch performance review data if reportUUID is present
  const { data: reviewData, isLoading: reviewLoading } = useFetchReview(
    reportUUID || ''
  );

  // Fetch document structure if we have both introArtifactUUID and docUUID
  const {
    data: documentsStructure,
    isLoading: documentLoading,
    error: documentError,
  } = useQuery({
    queryKey: ['documents-sidebar', introArtifactUUID],
    queryFn: () => {
      // Decode the URL-encoded repository name
      const vectorDBUrl = introArtifactUUID?.match(/%26/)
        ? introArtifactUUID.replace(/%26/g, '&')
        : introArtifactUUID;
      return fetchDocumentsSidebar({ VectorDBUrl: vectorDBUrl || '' });
    },
    enabled: !!introArtifactUUID,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Find the specific team if teamUUID is present
  const teamData = useMemo(() => {
    if (!teamUUID || !teamsData?.Teams) return null;
    return teamsData.Teams.find((team: any) => team.TeamUUID === teamUUID);
  }, [teamUUID, teamsData]);

  // Create enhanced dynamic values with resolved names
  const enhancedDynamicValues = useMemo(() => {
    const enhanced = { ...dynamicValues };

    // Decode the IntroArtifactUUID (repository name) to replace %26 with /
    if (introArtifactUUID) {
      const decodedRepoName = introArtifactUUID.replace(/%26/g, '/');
      enhanced[introArtifactUUID] = decodedRepoName;
    }

    if (userId && userData) {
      const userName =
        userData.FirstName && userData.LastName
          ? `${userData.FirstName} ${userData.LastName}`
          : userData.GithubUsername || userData.Email || 'User';
      enhanced[userId] = userName;
    }

    if (teamUUID && teamData) {
      enhanced[teamUUID] = teamData.TeamName || 'Team';
    }

    if (orgUUID && orgData) {
      const orgName = orgData.Org?.OrgName || 'Organization';
      enhanced[orgUUID] = orgName;
    }

    if (reportUUID && reviewData) {
      const reportName =
        reviewData.user?.name ||
        reviewData.user?.username ||
        reviewData.user?.github_username ||
        reviewData.user?.gitlab_username ||
        reviewData.user?.bitbucket_username ||
        'Performance Report';
      enhanced[reportUUID] = reportName;
    }

    if (boardId && boardTitle) {
      enhanced[boardId] = boardTitle;
    }

    if (docUUID && documentsStructure?.Documents) {
      // Search through the document structure to find the specific document item
      let documentName = 'Document';

      for (const doc of documentsStructure.Documents) {
        // Check if the UUID matches a section in this document
        const section = doc.Items.find(
          (item) => item.Uuid === docUUID && item.Type === 'SECTION'
        );
        if (section) {
          documentName = section.Title;
          break;
        }

        // Check if the UUID matches a subsection in this document
        const subsection = doc.Items.find(
          (item) => item.Uuid === docUUID && item.Type === 'SUBSECTION'
        );
        if (subsection) {
          documentName = subsection.Title;
          break;
        }
      }

      enhanced[docUUID] = documentName;
    }

    return enhanced;
  }, [
    dynamicValues,
    introArtifactUUID,
    userId,
    userData,
    teamUUID,
    teamData,
    orgUUID,
    orgData,
    reportUUID,
    reviewData,
    boardId,
    boardTitle,
    docUUID,
    documentsStructure,
  ]);

  // Generate breadcrumbs with enhanced dynamic values
  const breadcrumbs = useMemo(() => {
    const generated = generateBreadcrumbs(pathname, enhancedDynamicValues);

    // Apply custom labels if provided
    const mapped = generated.map((breadcrumb) => ({
      ...breadcrumb,
      label:
        (breadcrumb.href && customLabels[breadcrumb.href]) || breadcrumb.label,
    }));

    if (ellieChatTitle) {
      mapped.push({
        label: ellieChatTitle,
        href: `/ask-ellie?conversation=${ellieConversationId}`,
        isActive: true,
      });
    }

    return mapped;
  }, [
    pathname,
    enhancedDynamicValues,
    customLabels,
    ellieChatTitle,
    ellieConversationId,
  ]);

  const pageTitle = useMemo(() => {
    return getPageTitle(pathname, enhancedDynamicValues);
  }, [pathname, enhancedDynamicValues]);

  const isLoading =
    userLoading ||
    teamsLoading ||
    orgLoading ||
    reviewLoading ||
    documentLoading;

  return {
    breadcrumbs,
    pageTitle,
    currentPath: pathname,
    isLoading,
  };
}

/**
 * Hook to get just the page title with dynamic UUID resolution
 * @param dynamicValues - Optional dynamic values for route segments
 * @returns The page title
 */
export function useDynamicPageTitle(
  dynamicValues?: Record<string, string>
): string {
  const pathname = usePathname();

  return useMemo(() => {
    return getPageTitle(pathname, dynamicValues);
  }, [pathname, dynamicValues]);
}
